'use client'

import type { Persistor } from 'redux-persist'

import { setupListeners } from '@reduxjs/toolkit/query'
import { useEffect, useRef } from 'react'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'

import { type AppStore, makeStore, persistor } from './store'

// See https://redux.js.org/usage/nextjs#providing-the-store
// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function StoreProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const storeRef = useRef<AppStore | null>(null)
  const persistorRef = useRef<Persistor | null>(null)

  if (!storeRef.current) {
    // Create the store instance the first time this renders
    storeRef.current = makeStore()
    persistorRef.current = persistor(storeRef.current)
  }

  // Enable polling, refetchOnFocus, and refetchOnReconnect behaviors
  // Must be called in useEffect to ensure it runs on the client
  useEffect(() => {
    if (storeRef.current != null) {
      const unsubscribe = setupListeners(storeRef.current.dispatch)
      return unsubscribe
    }
  }, [])

  return (
    <Provider store={storeRef.current}>
      {/* IMPORTANT: loading must be null, not a visible component.
         PersistGate always renders `loading` during SSR (bootstrapped is
         false on the server). On the client it immediately flips to
         children in componentDidMount. A visible loading component
         creates server HTML that mismatches the client's first real
         render, triggering React 19's hydration recovery. That recovery
         corrupts Next.js's router cache in dev (Turbopack), causing
         Suspense boundaries to hang forever. With null, there is no
         server HTML to mismatch against.
         See: DES-282 */}
      <PersistGate
        loading={null}
        // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
        persistor={persistorRef.current!}
      >
        {children}
      </PersistGate>
    </Provider>
  )
}
